import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FirebaseStorageService } from '../services/firebase-storage.service';

/**
 * A colour as '#rrggbb', whichever way it was written.
 *
 * The detected background arrives as 'rgb(r, g, b)' from the canvas and the
 * fixed swatches are hex, so they have to be put in the same terms before
 * anything can ask whether two of them are the same colour.
 */
function toHex(color: string | null): string | null {
  if (!color) return null;
  const trimmed = color.trim().toLowerCase();
  if (trimmed.startsWith('#')) {
    return trimmed.length === 4
      ? '#' + trimmed.slice(1).split('').map((c) => c + c).join('')
      : trimmed;
  }
  const parts = trimmed.match(/\d+/g);
  if (!parts || parts.length < 3) return trimmed;
  return '#' + parts.slice(0, 3).map((n) => Number(n).toString(16).padStart(2, '0')).join('');
}

@Component({
  selector: 'app-logo-upload',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="logo-upload-container">
      <!-- Main Logo Preview -->
      <div class="logo-preview" (click)="onLogoAction()">
        <div class="logo-wrapper">
          <img 
            *ngIf="logoUrl && !showCropper" 
            [src]="logoUrl" 
            alt="Event logo" 
            class="logo-image"
            (error)="onImageError($event)"
          />
          <div *ngIf="!logoUrl && !showCropper" class="logo-placeholder">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21,15 16,10 5,21"/>
            </svg>
            <span>Add Logo</span>
          </div>
          <!--
            Shown without hover, and only once a logo exists. Invitations arrive
            with a logo we fetched from the organizer's own site, so the tile has
            to read as "ours to change" at a glance -- a hover-only affordance
            leaves them assuming the logo is fixed, and touch devices never get
            the hover at all.
          -->
          <div *ngIf="logoUrl && !showCropper" class="edit-badge" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 20h9"/>
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
            </svg>
          </div>
          <div class="upload-overlay">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17,8 12,3 7,8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span>{{ logoUrl ? 'Replace' : 'Upload' }}</span>
          </div>
        </div>
      </div>

      <!--
        One action. Where the picture comes from is a choice made after asking
        for it, not a second link sitting beside the first - two links read as
        two unrelated things, and the reader has to tell them apart before they
        know which one they wanted.
      -->
      <div class="logo-source">
        <button
          type="button"
          class="logo-hint"
          (click)="onLogoAction()"
          [disabled]="loadingExisting"
          [attr.aria-expanded]="canEditExisting ? showSourceMenu : null"
          [attr.aria-haspopup]="canEditExisting ? 'menu' : null"
        >
          {{ logoHintText }}
          <svg *ngIf="canEditExisting" class="caret" [class.up]="showSourceMenu" width="10" height="6"
               viewBox="0 0 10 6" fill="none" aria-hidden="true">
            <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.6"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div class="source-menu" *ngIf="showSourceMenu && !showCropper" role="menu">
          <button type="button" class="source" role="menuitem" (click)="chooseEditExisting()">
            <span class="glyph" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/>
                <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/>
              </svg>
            </span>
            <span class="text">
              <span class="title">Edit this logo</span>
              <span class="detail">Reframe or re-pad it — no file needed</span>
            </span>
          </button>

          <button type="button" class="source" role="menuitem" (click)="chooseUploadNew()">
            <span class="glyph" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17,8 12,3 7,8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </span>
            <span class="text">
              <span class="title">Upload a new one</span>
              <span class="detail">Choose a different picture</span>
            </span>
          </button>
        </div>
      </div>

      <!-- Cropper Modal -->
      <div *ngIf="showCropper" class="cropper-modal">
        <div class="cropper-overlay" (click)="cancelCrop()"></div>
        <div class="cropper-container" (click)="$event.stopPropagation()">
          <div class="cropper-header">
             <h3>Crop Logo</h3>
             <button type="button" class="close-btn" (click)="cancelCrop()">×</button>
           </div>
           
           <div class="cropper-instructions">
             <p>Drag to move the crop area or use the handles to resize. The logo will be cropped to a square.</p>
             <p class="fit-note" *ngIf="!isSquareSource">
               This logo is not square, so it opens zoomed to fit whole — nothing is cut.
               {{ detectedColor ? 'The margins start as the logo\'s own background colour.' : 'No single background colour could be read, so the margins start clear.' }}
               Change them below, or zoom in if you would rather crop it.
             </p>
           </div>
          
          <!-- Scrollable content only -->
          <div class="cropper-content" #cropperContent>
            <div class="cropper-wrapper" #cropperWrapper [style.width.px]="canvasWidth" [style.height.px]="canvasHeight">
              <canvas #cropperCanvas class="cropper-canvas"></canvas>
              <div class="crop-overlay">
                <!-- Shaded regions outside the crop square -->
                <div class="shade shade-top" [style.height.px]="cropArea.y" [style.width.px]="canvasWidth"></div>
                <div class="shade shade-left" [style.top.px]="cropArea.y" [style.height.px]="cropArea.size" [style.width.px]="cropArea.x"></div>
                <div class="shade shade-right" [style.top.px]="cropArea.y" [style.height.px]="cropArea.size" [style.left.px]="cropArea.x + cropArea.size" [style.width.px]="getRightShadeWidth()"></div>
                <div class="shade shade-bottom" [style.top.px]="cropArea.y + cropArea.size" [style.height.px]="getBottomShadeHeight()" [style.width.px]="canvasWidth"></div>
                <!-- Actual crop area -->
                <div class="crop-area" 
                     [style.left.px]="cropArea.x" 
                     [style.top.px]="cropArea.y"
                     [style.width.px]="cropArea.size"
                     [style.height.px]="cropArea.size"
                     (mousedown)="startDrag($event)"
                     (touchstart)="startDrag($event)">
                  <div class="crop-handle crop-handle-nw" (mousedown)="startResize($event, 'nw')" (touchstart)="startResize($event, 'nw')"></div>
                  <div class="crop-handle crop-handle-ne" (mousedown)="startResize($event, 'ne')" (touchstart)="startResize($event, 'ne')"></div>
                  <div class="crop-handle crop-handle-sw" (mousedown)="startResize($event, 'sw')" (touchstart)="startResize($event, 'sw')"></div>
                  <div class="crop-handle crop-handle-se" (mousedown)="startResize($event, 'se')" (touchstart)="startResize($event, 'se')"></div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Controls always visible -->
          <div class="cropper-controls">
            <!--
              What fills the space the logo does not reach. Only worth showing
              for a picture that has margins to fill.
            -->
            <div class="margins" *ngIf="!isSquareSource">
              <span class="margins-label">Margins</span>
              <button
                type="button"
                class="swatch"
                *ngFor="let option of marginOptions"
                [class.on]="marginChoice === option.id"
                [style.background]="option.color || 'transparent'"
                [title]="option.title"
                (click)="chooseMargin(option.id)"
              >
                <span class="none-mark" *ngIf="!option.color">&#8709;</span>
              </button>
              <label class="swatch custom" [class.on]="marginChoice === 'custom'"
                     [style.background]="customColor" title="Mix your own colour">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     [attr.stroke]="customIsLight ? '#12332F' : '#ffffff'"
                     stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
                  <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
                  <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
                  <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
                  <line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/>
                  <line x1="17" y1="16" x2="23" y2="16"/>
                </svg>
                <input type="color" [value]="customColor" (input)="chooseCustom($event)" />
              </label>
              <button type="button" class="pick-from" *ngIf="sampledColors.length"
                      (click)="showSampled = !showSampled">
                From the picture
              </button>
            </div>
            <div class="sampled" *ngIf="!isSquareSource && showSampled && sampledColors.length">
              <button
                type="button"
                class="swatch"
                *ngFor="let color of sampledColors"
                [class.on]="marginChoice === 'custom' && customColor === color"
                [style.background]="color"
                [title]="color"
                (click)="useSampled(color)"
              ></button>
            </div>

            <div class="zoom-controls">
              <button type="button" (click)="zoomOut($event)" [disabled]="zoom <= 0.1">-</button>
              <span>{{ (zoom * 100).toFixed(0) }}%</span>
              <button type="button" (click)="zoomIn($event)" [disabled]="zoom >= 3">+</button>
              <button type="button" class="fit-btn" (click)="fitWholeLogo($event)">Fit whole logo</button>
            </div>
            <div class="crop-actions">
              <button type="button" class="cancel-btn" (click)="cancelCrop($event)">Cancel</button>
              <button type="button" class="crop-btn" (click)="applyCrop($event)">Apply Crop</button>
            </div>
          </div>
        </div>
      </div>
      
      <input 
        #fileInput 
        type="file" 
        accept="image/png,image/jpeg,image/jpg" 
        style="display: none;"
        (change)="onFileSelected($event)"
      />
      
      <div *ngIf="isUploading" class="upload-progress">
        <div class="spinner"></div>
        <span>Uploading...</span>
      </div>
      
      <div *ngIf="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  `,
  styles: [`
    .logo-upload-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }
    
    .logo-preview {
      cursor: pointer;
      border-radius: 8px;
      /* visible, not hidden: the edit badge deliberately overhangs the tile */
      overflow: visible;
      transition: all 0.2s ease;
    }
    
    .logo-preview:hover {
      transform: scale(1.02);
    }
    
    .logo-wrapper {
      position: relative;
      width: 100px;
      height: 100px;
      border: 2px dashed rgba(18, 51, 47, 0.18);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #FFF6EE;
      transition: all 0.2s ease;
    }
    
    .logo-wrapper:hover {
      border-color: #1F6F66;
      background: rgba(109, 191, 179, 0.06);
    }
    
    .logo-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 6px;
    }
    
    .logo-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      color: #5E5A57;
      font-size: 12px;
    }
    
    /* Covers the tile on hover and says what the click does, rather than
       leaving a bare glyph to be interpreted. */
    .upload-overlay {
      position: absolute;
      inset: 0;
      background: rgba(17, 24, 39, 0.72);
      color: white;
      border-radius: 6px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.2px;
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    .logo-preview:hover .upload-overlay,
    .logo-preview:focus-visible .upload-overlay {
      opacity: 1;
    }

    /* Always visible: the only cue on a touch device, where hover never fires. */
    .edit-badge {
      position: absolute;
      right: -6px;
      bottom: -6px;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      background: #12332F;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
      border: 2px solid #fff;
      pointer-events: none;
    }

    .logo-hint {
      background: none;
      border: none;
      padding: 0;
      color: #1F6F66;
      font-size: 12px;
      font-weight: 600;
      text-align: center;
      cursor: pointer;
      text-decoration: underline;
      text-underline-offset: 2px;
      font-family: inherit;
    }

    .logo-hint:hover {
      color: #185A53;
    }

    .cropper-modal {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 20px 20px 20px 20px;
      overflow-y: auto;
      box-sizing: border-box;
    }
    
    .cropper-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1;
    }
    
    .cropper-container {
      background: #ffffff;
      border-radius: 16px;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
      max-width: 90vw; 
      max-height: 75vh; /* fix missing unit so container is constrained to viewport */
      width: min(100%, 960px);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border: 1px solid #e8e8e8;
      position: relative;
      z-index: 2;
      margin: 20px auto 0 auto;
      box-sizing: border-box;
    }
    
    /* Teal, from lib/utils/aurora_palette.dart. #004F6C is pre-rebrand: a
       colour that appears nowhere else in the admin, on the one dialog an
       organizer's logo passes through. */
    .cropper-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      background: #12332F;
    }
    
    .cropper-header h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      letter-spacing: -0.01em;
      color: #FFF6EE;
    }
    
    .close-btn {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #FFF6EE;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: background-color 0.2s;
    }
    
    .close-btn:hover {
      background: rgba(255, 246, 238, 0.16);
    }
    
    .cropper-instructions {
      padding: 20px 24px;
      border-bottom: 1px solid #f0f0f0;
      background: #FFF6EE;
    }
    
    .cropper-instructions p {
      margin: 0;
      font-size: 15px;
      color: #555;
      text-align: center;
      line-height: 1.5;
      font-weight: 500;
    }
    
    .cropper-content {
      padding: 24px 16px;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 0;
      background: #ffffff;
      max-height: calc(75vh - 220px); /* allow a little more room so no scroll is needed */
      overflow: auto; /* scroll only image area */
      touch-action: pan-x pan-y; /* Allow panning but prevent zooming */
    }
    
    .cropper-wrapper {
      position: relative;
      display: inline-block;
      margin-bottom: 16px;
      flex: 1 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 16px;
      isolation: isolate;
      clip-path: inset(0);
      width:100%;
    }
    
    .cropper-canvas {
      display: block;
      max-width: 100%;
      max-height: 100%;
      border: 3px solid #e8e8e8;
      border-radius: 16px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.15);
      background:#fafafa;
      position: relative;
      width:100%;
      height:auto;
    }
    
    .crop-overlay {
      position:absolute;
      top:0;
      left:0;
      width:100%;
      height:100%;
      pointer-events:none;
    }
    
    .crop-area {
      position:absolute;
      border:2px solid #2e64ff;
      background:rgba(46,100,255,0.08);
      pointer-events:all;
      cursor:move;
      border-radius:4px;
      z-index:2;
      box-shadow:0 0 0 1px rgba(255,255,255,0.6), 0 0 0 9999px rgba(0,0,0,0);
      touch-action: none; /* Prevent default touch behaviors */
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
    
    .crop-handle {
      position:absolute;
      width:14px;
      height:14px;
      background:#fff;
      border:2px solid #2e64ff;
      border-radius:50%;
      box-shadow:0 2px 6px rgba(0,0,0,0.3);
      cursor:pointer;
      pointer-events:all;
      touch-action: none; /* Prevent default touch behaviors */
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
    .crop-handle-nw { top:-8px; left:-8px; cursor:nw-resize; }
    .crop-handle-ne { top:-8px; right:-8px; cursor:ne-resize; }
    .crop-handle-sw { bottom:-8px; left:-8px; cursor:sw-resize; }
    .crop-handle-se { bottom:-8px; right:-8px; cursor:se-resize; }
    
    /* Shaded regions */
    .shade {
      position:absolute;
      left:0;
      background:rgba(0,0,0,0.55);
      pointer-events:none;
      z-index:1;
    }
    .shade-left {
      left: 0;
    }
    .shade-right {
    }
    .shade-top {
      top: 0;
    }
    .shade-bottom {
      left: 0;
    }
    
    .cropper-controls {
      display: flex;
      /* Wraps, because the margin swatches are a row of their own above the
         zoom and the buttons - squeezed in beside them they had no room. */
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      padding: 16px 24px 20px 24px; /* add horizontal + bottom padding so buttons not flush with edges */
      border-top: 1px solid #f0f0f0;
      background: #fafafa;
      flex-shrink: 0;
      position: sticky; /* in case content taller than viewport */
      bottom: 0;
    }

      .margins {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
      margin-top: 10px;
    }
    .margins-label {
      font-size: 11px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #8A5A08;
      font-weight: 700;
      margin-right: 2px;
    }
    .sampled {
      width: 100%;
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      margin-top: 8px;
      padding-left: 62px;
    }
    .swatch {
      width: 26px;
      height: 26px;
      border-radius: 8px;
      border: 1px solid rgba(18, 51, 47, 0.2);
      padding: 0;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    .swatch.on {
      border-color: #1F6F66;
      border-width: 2.5px;
      box-shadow: 0 0 0 2px rgba(31, 111, 102, 0.15);
    }
    .swatch .none-mark { color: #8F8C88; font-size: 14px; line-height: 1; }
    .swatch.custom { position: relative; }
    /* The native picker does the mixing; the tile just wears the result. */
    .swatch.custom input[type='color'] {
      position: absolute;
      inset: -4px;
      width: calc(100% + 8px);
      height: calc(100% + 8px);
      border: none;
      padding: 0;
      background: none;
      cursor: pointer;
      opacity: 0;
    }
    .pick-from {
      background: none;
      border: none;
      padding: 0 4px;
      font-size: 11.5px;
      font-weight: 600;
      color: #1F6F66;
      cursor: pointer;
      text-decoration: underline;
    }

    .fit-btn {
      width: auto !important;
      padding: 0 12px;
      font-size: 12px !important;
      font-weight: 600;
      white-space: nowrap;
    }
    .fit-note {
      color: #8A5A08;
      font-size: 12.5px;
      line-height: 1.5;
      margin-top: 6px;
    }

    /* --------------------------------------------------------- logo source
       One link, and the two places a picture can come from. A popover rather
       than two links stacked in the page: a choice that only exists once you
       ask for it should not be sitting there before you do. */
    .logo-source { position: relative; display: flex; flex-direction: column; align-items: center; }
    .logo-hint .caret { margin-left: 5px; transition: transform 0.15s ease; }
    .logo-hint .caret.up { transform: rotate(180deg); }

    .source-menu {
      position: absolute;
      top: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%);
      z-index: 20;
      display: flex;
      flex-direction: column;
      gap: 2px;
      width: max-content;
      max-width: min(320px, 86vw);
      padding: 6px;
      background: #fff;
      border: 1px solid rgba(18, 51, 47, 0.1);
      border-radius: 16px;
      box-shadow: 0 14px 32px rgba(18, 51, 47, 0.14);
      animation: source-menu-in 0.14s ease-out;
    }
    @keyframes source-menu-in {
      from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
      to   { opacity: 1; transform: translateX(-50%) translateY(0); }
    }
    /* Points back at the link it came from, so it reads as attached to it. */
    .source-menu::before {
      content: '';
      position: absolute;
      top: -5px;
      left: 50%;
      width: 9px;
      height: 9px;
      margin-left: -4.5px;
      background: #fff;
      border-left: 1px solid rgba(18, 51, 47, 0.1);
      border-top: 1px solid rgba(18, 51, 47, 0.1);
      transform: rotate(45deg);
    }

    .source-menu .source {
      display: flex;
      align-items: center;
      gap: 11px;
      width: 100%;
      background: none;
      border: none;
      border-radius: 12px;
      padding: 9px 10px;
      cursor: pointer;
      text-align: left;
      transition: background 0.12s ease;
    }
    .source-menu .source:hover,
    .source-menu .source:focus-visible {
      background: rgba(31, 111, 102, 0.08);
      outline: none;
    }
    .source-menu .glyph {
      flex: none;
      width: 34px;
      height: 34px;
      border-radius: 11px;
      display: grid;
      place-items: center;
      background: rgba(31, 111, 102, 0.1);
      color: #1F6F66;
    }
    .source-menu .text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
    .source-menu .title { font-size: 13.5px; font-weight: 600; color: #12332F; line-height: 1.3; }
    .source-menu .detail { font-size: 11.5px; color: #8F8C88; line-height: 1.35; }

    /* Mobile responsive - stack controls vertically */
    @media (max-width: 768px) {
      .cropper-controls {
        flex-direction: column;
        gap: 12px;
        padding: 16px 16px 20px 16px;
      }
      
      .zoom-controls {
        order: 1;
        justify-content: center;
      }
      
          .crop-actions {
        order: 2;
        width: 100%;
        justify-content: center;
        gap: 12px;
      }
      
      .cancel-btn, .crop-btn {
        flex: 1;
        max-width: 120px;
      }
    }
    
    .zoom-controls {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .zoom-controls button {
      width: 40px;
      height: 40px;
      border: 2px solid #e0e0e0;
      background: white;
      border-radius: 8px;
      cursor: pointer;
      font-weight: bold;
      font-size: 16px;
      position: relative;
      z-index: 10;
      transition: all 0.2s ease;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    
    .zoom-controls button:hover {
      background: #FFF6EE;
      border-color: #1F6F66;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
    
    .zoom-controls button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    
    .zoom-controls span {
      min-width: 60px;
      text-align: center;
      font-size: 15px;
      font-weight: 600;
      color: #333;
    }
    
    .crop-actions {
      display: flex;
      gap: 8px;
    }
    
    .cancel-btn, .crop-btn {
      padding: 12px 24px;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      font-size: 15px;
      font-weight: 600;
      position: relative;
      z-index: 10;
      transition: all 0.2s ease;
    }
    
    .cancel-btn {
      background: #FFF6EE;
      color: #555;
      border: 2px solid #e0e0e0;
    }
    
    /* Accent-deep: the button sits on the light control strip, where the
       palette's rule is the deep accent rather than the light one. */
    .crop-btn {
      background: #1F6F66;
      color: #FFF6EE;
      box-shadow: 0 4px 12px rgba(31, 111, 102, 0.28);
    }
    
    .cancel-btn:hover {
      background: #e9ecef;
      border-color: #d0d0d0;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    .crop-btn:hover {
      background: #185A53;
      box-shadow: 0 6px 16px rgba(31, 111, 102, 0.38);
      transform: translateY(-2px);
    }
    
    .cancel-btn:active, .crop-btn:active {
      transform: translateY(0);
    }
    
    .upload-progress {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #667eea;
      font-size: 14px;
    }
    
    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid #e0e0e0;
      border-top: 2px solid #667eea;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    .error-message {
      color: #d32f2f;
      font-size: 12px;
      text-align: center;
    }
  `]
})
export class LogoUploadComponent implements OnInit, OnChanges {
  @Input() eventId: string | null = null;
  @Input() currentLogoUrl: string | null = null;
  /*
   * Wording under the tile when a logo is already present. The default suits
   * the space-claim invitation, where the logo was fetched from the organizer's
   * own site rather than chosen by them -- so it says where it came from and
   * that they decide, not just that the tile is clickable.
   */
  @Input() replaceHint = 'We found this logo on your website — click to replace it';
  /**
   * Loads the logo already stored, so it can be re-cropped.
   *
   * A function rather than a URL because the bytes cannot simply be
   * fetched: the storage bucket sends no CORS headers, so an image loaded
   * straight from its URL taints the canvas and cannot be read back. The
   * admin board passes a loader that goes through its own API; the public
   * claim wizard passes nothing, and simply does not offer the button.
   */
  @Input() existingImageLoader: (() => Promise<string | null>) | null = null;
  @Output() logoUploaded = new EventEmitter<string>();
  @Output() logoRemoved = new EventEmitter<void>();
  @ViewChild('fileInput', { static: true }) fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('cropperCanvas', { static: false }) cropperCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('cropperWrapper', { static: false }) cropperWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('cropperContent', { static: false }) cropperContent!: ElementRef<HTMLDivElement>;

  logoUrl: string | null = null;
  isUploading = false;
  errorMessage = '';
  showCropper = false;
  zoom = 1;
  
  // Cropping state
  private originalImage: HTMLImageElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private isDragging = false;
  private isResizing = false;
  private resizeHandle = '';
  private dragStart = { x: 0, y: 0 };
  private originalCropArea = { x: 0, y: 0, size: 0 };
  
  cropArea = { x: 0, y: 0, size: 200 };
  /** Colour the margins take when a non-square logo is fitted whole. Null
      means the source already carries transparency, so it is left alone. */
  padColor: string | null = null;
  isSquareSource = true;
  loadingExisting = false;
  /** Open while the reader decides where the next picture comes from. */
  showSourceMenu = false;

  /** Which margin colour is in use. 'logo' is whatever was read off the image. */
  marginChoice: 'logo' | 'white' | 'cream' | 'black' | 'none' | 'custom' = 'logo';
  /** Whatever the reader mixed, as the colour input speaks it. */
  customColor = '#ffffff';
  /** Colours taken from the picture, for when none of the fixed ones fit. */
  sampledColors: string[] = [];
  showSampled = false;

  /** The fixed choices, in the order they are shown. */
  get marginOptions(): { id: 'logo' | 'white' | 'cream' | 'black' | 'none'; color: string | null; title: string }[] {
    return [
      ...(this.detectedColor
        ? [{ id: 'logo' as const, color: this.detectedColor, title: "The logo's own background" }]
        : []),
      { id: 'white' as const, color: '#ffffff', title: 'White' },
      { id: 'cream' as const, color: '#fff6ee', title: 'Cream' },
      { id: 'black' as const, color: '#000000', title: 'Black' },
      { id: 'none' as const, color: null, title: 'Transparent' },
    ].filter(
      // A fixed colour identical to the logo's own is the same tile twice, and
      // the reader cannot tell which they clicked. The logo's own tile is of
      // course that colour, and is the one that stays.
      (option) =>
        option.id === 'logo' ||
        !option.color ||
        toHex(option.color) !== toHex(this.detectedColor)
    );
  }

  /** Whether the mixed colour is light, so its glyph can be read against it. */
  get customIsLight(): boolean {
    const hex = toHex(this.customColor);
    if (!hex) return true;
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
    // Rec. 601 luma: green carries most of the perceived brightness.
    return (r * 299 + g * 587 + b * 114) / 1000 > 150;
  }

  /** What was read off the image, kept apart from what is currently chosen. */
  detectedColor: string | null = null;

  /** Pick one of the fixed colours. */
  chooseMargin(id: 'logo' | 'white' | 'cream' | 'black' | 'none'): void {
    this.marginChoice = id;
    this.padColor = this.marginOptions.find((o) => o.id === id)?.color ?? null;
    this.drawImage();
  }

  /** Take whatever the colour input is now showing. */
  chooseCustom(event: Event): void {
    this.customColor = (event.target as HTMLInputElement).value;
    this.marginChoice = 'custom';
    this.padColor = this.customColor;
    this.drawImage();
  }

  /** Take one of the colours read out of the picture itself. */
  useSampled(color: string): void {
    this.customColor = color;
    this.marginChoice = 'custom';
    this.padColor = color;
    this.drawImage();
  }
  private selectedFile: File | null = null;

  // Canvas dimensions for shading calculations
  canvasWidth = 0;
  canvasHeight = 0;

  // Store bound function references for proper cleanup
  private boundMouseMove = (event: MouseEvent) => this.onMouseMove(event);
  private boundMouseUp = () => this.onMouseUp();
  private boundTouchMove = (event: TouchEvent) => this.onTouchMove(event);
  private boundTouchEnd = () => this.onTouchEnd();

  constructor(
    private firebaseStorage: FirebaseStorageService,
    private host: ElementRef<HTMLElement>
  ) {}

  /* Clicking anywhere else, or pressing Escape, closes the chooser - the two
     ways out every menu is expected to have. Clicks inside are left alone, so
     the link that opened it can still toggle it shut. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showSourceMenu) return;
    if (!this.host.nativeElement.contains(event.target as Node)) this.showSourceMenu = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.showSourceMenu = false;
  }

  ngOnInit(): void {
    this.logoUrl = this.currentLogoUrl;
  }

  ngOnChanges(): void {
    if (this.currentLogoUrl !== this.logoUrl) {
      this.logoUrl = this.currentLogoUrl;
    }
  }

  onLogoClick(): void {
    this.fileInput.nativeElement.click();
  }

  /** Whether there is a stored logo this board is allowed to re-open. */
  get canEditExisting(): boolean {
    return !!(this.logoUrl && this.existingImageLoader);
  }

  get logoHintText(): string {
    if (this.loadingExisting) return 'Opening…';
    if (!this.logoUrl) return 'Click to upload your event logo';
    // The claim wizard has no loader, so it keeps its own wording: it is
    // telling an organizer where the logo came from, not offering a choice.
    return this.canEditExisting ? 'Change the logo' : this.replaceHint;
  }

  /**
   * The single way in.
   *
   * With a logo already there and somewhere to load it from, this asks which
   * picture is meant. With nothing to edit, there is no question worth posing,
   * so it goes straight to the file picker.
   */
  onLogoAction(): void {
    if (this.loadingExisting) return;
    if (!this.canEditExisting) {
      this.showSourceMenu = false;
      this.onLogoClick();
      return;
    }
    this.showSourceMenu = !this.showSourceMenu;
  }

  chooseEditExisting(): void {
    this.showSourceMenu = false;
    this.adjustExisting();
  }

  chooseUploadNew(): void {
    this.showSourceMenu = false;
    this.onLogoClick();
  }

  async onFileSelected(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      this.errorMessage = 'Please select a PNG, JPG, or JPEG image file';
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 1 * 1024 * 1024) {
      this.errorMessage = 'File size must be less than 1MB';
      return;
    }

    this.selectedFile = file;
    this.errorMessage = '';
    await this.showCropperModal();
    
    // Reset file input
    target.value = '';
  }

  /**
   * Re-crop the logo that is already on the space.
   *
   * The loader hands back a data URL, which draws into the canvas without
   * tainting it - the whole reason the bytes come through our own API.
   */
  async adjustExisting(): Promise<void> {
    if (!this.existingImageLoader || this.loadingExisting) return;
    this.loadingExisting = true;
    this.errorMessage = '';
    try {
      const dataUrl = await this.existingImageLoader();
      if (!dataUrl) {
        this.errorMessage = 'Could not open the current logo. Upload a new file instead.';
        return;
      }
      this.selectedFile = null;
      this.pendingSource = dataUrl;
      await this.showCropperModal();
    } catch {
      this.errorMessage = 'Could not open the current logo. Upload a new file instead.';
    } finally {
      this.loadingExisting = false;
    }
  }

  /** The image the cropper is about to open, as a URL the canvas can load. */
  private pendingSource: string | null = null;

  private async showCropperModal(): Promise<void> {
    console.log('Showing cropper modal');
    this.showCropper = true;
    
    // Wait for the canvas to be available
    setTimeout(() => {
      this.initializeCropper();
    }, 100);
  }

  private async initializeCropper(): Promise<void> {
    const source =
      this.pendingSource ?? (this.selectedFile ? URL.createObjectURL(this.selectedFile) : null);
    if (!source || !this.cropperCanvas) return;

    const img = new Image();
    img.onload = () => {
      this.originalImage = img;
      this.isSquareSource = img.width === img.height;
      this.detectedColor = this.sampleBackground(img);
      this.padColor = this.detectedColor;
      this.marginChoice = this.detectedColor ? 'logo' : 'none';
      this.sampledColors = this.samplePalette(img);
      this.customColor = this.detectedColor ?? this.sampledColors[0] ?? '#fff6ee';
      this.showSampled = false;
      this.setupCanvas();
      this.initializeCropArea();
      /* Open showing the whole logo. A wide wordmark cropped to a square loses
         its ends, and a reader who never touches the zoom should not be the one
         who finds that out in the app. */
      if (!this.isSquareSource) this.fitWholeLogo();
      this.drawImage();
      this.pendingSource = null;
    };
    img.onerror = () => {
      this.errorMessage = 'That image could not be opened.';
      this.showCropper = false;
      this.pendingSource = null;
    };
    img.src = source;
  }

  /**
   * What the margins should be filled with, read off the image itself.
   *
   * A logo that already carries transparency keeps it - padding a cut-out mark
   * with a colour would put a box around it. Anything else (a JPEG wordmark on
   * white, say) is padded with its own corner colour, so the fitted square
   * looks like one piece rather than a logo sitting on a patch.
   *
   * Returns null to mean "leave it transparent".
   */
  private sampleBackground(img: HTMLImageElement): string | null {
    const probe = document.createElement('canvas');
    const size = 32;
    probe.width = size;
    probe.height = size;
    const pctx = probe.getContext('2d', { willReadFrequently: true });
    if (!pctx) return null;

    try {
      pctx.drawImage(img, 0, 0, size, size);
      const { data } = pctx.getImageData(0, 0, size, size);
      const at = (x: number, y: number) => {
        const i = (y * size + x) * 4;
        return { r: data[i], g: data[i + 1], b: data[i + 2], a: data[i + 3] };
      };
      const corners = [at(0, 0), at(size - 1, 0), at(0, size - 1), at(size - 1, size - 1)];

      // Any see-through corner means the source is a cut-out: keep it that way.
      if (corners.some((c) => c.a < 250)) return null;

      // Corners that disagree are a picture, not a background. Padding it with
      // an average would invent a colour that is in none of them.
      const spread = (pick: (c: { r: number; g: number; b: number }) => number) =>
        Math.max(...corners.map(pick)) - Math.min(...corners.map(pick));
      if (spread((c) => c.r) > 24 || spread((c) => c.g) > 24 || spread((c) => c.b) > 24) {
        return null;
      }

      const avg = (pick: (c: { r: number; g: number; b: number }) => number) =>
        Math.round(corners.reduce((n, c) => n + pick(c), 0) / corners.length);
      return `rgb(${avg((c) => c.r)}, ${avg((c) => c.g)}, ${avg((c) => c.b)})`;
    } catch {
      // A tainted canvas cannot be read. Transparent is the safe answer.
      return null;
    }
  }

  /**
   * Colours the picture is actually made of, for when its corners disagreed.
   *
   * A photograph has no background colour to read, but one of its own shades
   * still makes a better margin than any fixed swatch. Counted in coarse
   * buckets so a gradient does not fill the row with the same colour six times.
   */
  private samplePalette(img: HTMLImageElement, take = 5): string[] {
    const probe = document.createElement('canvas');
    const size = 32;
    probe.width = size;
    probe.height = size;
    const pctx = probe.getContext('2d', { willReadFrequently: true });
    if (!pctx) return [];

    try {
      pctx.drawImage(img, 0, 0, size, size);
      const { data } = pctx.getImageData(0, 0, size, size);
      const step = 8;
      const counts = new Map<number, number>();
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 250) continue;
        const key =
          ((data[i] / step) | 0) * 65536 + ((data[i + 1] / step) | 0) * 256 + ((data[i + 2] / step) | 0);
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
      const hex = (n: number) => n.toString(16).padStart(2, '0');
      return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, take)
        .map(([key]) => {
          // Back to the middle of the bucket, so nothing comes out darker than
          // everything that landed in it.
          const half = step / 2;
          const r = Math.min(255, ((key / 65536) | 0) * step + half);
          const g = Math.min(255, (((key / 256) | 0) % 256) * step + half);
          const b = Math.min(255, (key % 256) * step + half);
          return `#${hex(r)}${hex(g)}${hex(b)}`;
        });
    } catch {
      // A tainted canvas cannot be read; the fixed colours still work.
      return [];
    }
  }

  /** Zoom so the whole logo sits inside the crop square, margins and all. */
  fitWholeLogo(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    if (!this.originalImage || !this.canvas) return;
    const longest = Math.max(this.originalImage.width, this.originalImage.height);
    if (!longest) return;
    this.zoom = Math.max(0.1, Math.min(3, this.cropArea.size / longest));
    this.drawImage();
  }

  private setupCanvas(): void {
    if (!this.originalImage || !this.cropperCanvas) return;
    this.canvas = this.cropperCanvas.nativeElement;
    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) return;

    // Get the full modal width (accounting for padding)
    const modalWidth = Math.min(window.innerWidth * 0.9, 960);
    const contentPadding = 80; // 24px padding left + 24px padding right from cropper-content + extra margin
    const availableWidth = modalWidth - contentPadding;

    // Reserve vertical space (header + instructions + controls)
    let maxDisplayHeight = (window.innerHeight * 0.75) - 260;
    maxDisplayHeight = Math.max(200, Math.min(maxDisplayHeight, 420));

    // Canvas fills the full available width, height is constrained by maxDisplayHeight
    let canvasWidth = availableWidth;
    let canvasHeight = maxDisplayHeight;

    this.canvas.width = Math.round(canvasWidth);
    this.canvas.height = Math.round(canvasHeight);
    this.canvasWidth = this.canvas.width;
    this.canvasHeight = this.canvas.height;

    // Ensure CSS matches intrinsic size (so it visually fills)
    this.canvas.style.width = this.canvasWidth + 'px';
    this.canvas.style.height = this.canvasHeight + 'px';
  }

  private drawImage(): void {
    if (!this.ctx || !this.originalImage || !this.canvas) return;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    /* Paint the margins the export will produce, so the preview is the result
       rather than an approximation of it. */
    if (this.padColor && this.cropArea.size) {
      this.ctx.fillStyle = this.padColor;
      this.ctx.fillRect(this.cropArea.x, this.cropArea.y, this.cropArea.size, this.cropArea.size);
    }

    // Apply zoom transformation
    const scaledWidth = this.originalImage.width * this.zoom;
    const scaledHeight = this.originalImage.height * this.zoom;
    
    const offsetX = (this.canvas.width - scaledWidth) / 2;
    const offsetY = (this.canvas.height - scaledHeight) / 2;
    
    this.ctx.drawImage(
      this.originalImage,
      offsetX, offsetY,
      scaledWidth, scaledHeight
    );
  }

  private initializeCropArea(): void {
    if (!this.canvas) return;

    // Smaller initial crop so it fits without scrolling and feels centered
    const shorterSide = Math.min(this.canvas.width, this.canvas.height);
    // Use 40% of shorter side (previously 60%) but clamp between 120 and (shorterSide - 60)
    let cropSize = shorterSide * 0.4;
    cropSize = Math.min(cropSize, shorterSide - 60);
    cropSize = Math.max(cropSize, 120);

    this.cropArea.size = cropSize;

    // Center the crop square
    this.cropArea.x = Math.max(0, (this.canvas.width - this.cropArea.size) / 2);
    this.cropArea.y = Math.max(0, (this.canvas.height - this.cropArea.size) / 2);
  }

  zoomIn(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.zoom < 3) {
      this.zoom += 0.1;
      this.drawImage();
    }
  }

  zoomOut(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.zoom > 0.1) {
      this.zoom = Math.max(0.1, this.zoom - 0.1);
      this.drawImage();
    }
  }

  startDrag(event: MouseEvent | TouchEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.isDragging = true;
    
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    
    this.dragStart = { x: clientX, y: clientY };
    this.originalCropArea = { ...this.cropArea };
    
    if ('touches' in event) {
      document.addEventListener('touchmove', this.boundTouchMove, { passive: false });
      document.addEventListener('touchend', this.boundTouchEnd);
    } else {
      document.addEventListener('mousemove', this.boundMouseMove);
      document.addEventListener('mouseup', this.boundMouseUp);
    }
  }

  startResize(event: MouseEvent | TouchEvent, handle: string): void {
    event.stopPropagation();
    event.preventDefault();
    this.isResizing = true;
    this.resizeHandle = handle;
    
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    
    this.dragStart = { x: clientX, y: clientY };
    this.originalCropArea = { ...this.cropArea };
    
    if ('touches' in event) {
      document.addEventListener('touchmove', this.boundTouchMove, { passive: false });
      document.addEventListener('touchend', this.boundTouchEnd);
    } else {
      document.addEventListener('mousemove', this.boundMouseMove);
      document.addEventListener('mouseup', this.boundMouseUp);
    }
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isResizing && !this.isDragging || !this.canvas) return;

    const deltaX = event.clientX - this.dragStart.x;
    const deltaY = event.clientY - this.dragStart.y;
    
    if (this.isDragging) {
      // Handle dragging
      this.cropArea.x = this.originalCropArea.x + deltaX;
      this.cropArea.y = this.originalCropArea.y + deltaY;
    } else if (this.isResizing) {
      // Handle resizing
      if (this.resizeHandle.includes('e')) {
        this.cropArea.size = Math.max(100, this.originalCropArea.size + deltaX);
      }
      if (this.resizeHandle.includes('w')) {
        const newSize = Math.max(100, this.originalCropArea.size - deltaX);
        this.cropArea.x = this.originalCropArea.x + (this.originalCropArea.size - newSize);
        this.cropArea.size = newSize;
      }
      if (this.resizeHandle.includes('s')) {
        this.cropArea.size = Math.max(100, this.originalCropArea.size + deltaY);
      }
      if (this.resizeHandle.includes('n')) {
        const newSize = Math.max(100, this.originalCropArea.size - deltaY);
        this.cropArea.y = this.originalCropArea.y + (this.originalCropArea.size - newSize);
        this.cropArea.size = newSize;
      }
    }
    
    // Keep crop area within canvas bounds
    this.cropArea.x = Math.max(0, Math.min(this.canvas.width - this.cropArea.size, this.cropArea.x));
    this.cropArea.y = Math.max(0, Math.min(this.canvas.height - this.cropArea.size, this.cropArea.y));
    
    // Ensure crop area doesn't go outside the canvas
    if (this.cropArea.x < 0) this.cropArea.x = 0;
    if (this.cropArea.y < 0) this.cropArea.y = 0;
    if (this.cropArea.x + this.cropArea.size > this.canvas.width) {
      this.cropArea.x = this.canvas.width - this.cropArea.size;
    }
    if (this.cropArea.y + this.cropArea.size > this.canvas.height) {
      this.cropArea.y = this.canvas.height - this.cropArea.size;
    }
  }

  private onMouseUp(): void {
    this.isResizing = false;
    this.isDragging = false;
    this.cleanupEventListeners();
  }

  private onTouchMove(event: TouchEvent): void {
    event.preventDefault();
    if (!this.isResizing && !this.isDragging || !this.canvas) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.dragStart.x;
    const deltaY = touch.clientY - this.dragStart.y;
    
    if (this.isDragging) {
      // Handle dragging
      this.cropArea.x = this.originalCropArea.x + deltaX;
      this.cropArea.y = this.originalCropArea.y + deltaY;
    } else if (this.isResizing) {
      // Handle resizing
      if (this.resizeHandle.includes('e')) {
        this.cropArea.size = Math.max(100, this.originalCropArea.size + deltaX);
      }
      if (this.resizeHandle.includes('w')) {
        const newSize = Math.max(100, this.originalCropArea.size - deltaX);
        this.cropArea.x = this.originalCropArea.x + (this.originalCropArea.size - newSize);
        this.cropArea.size = newSize;
      }
      if (this.resizeHandle.includes('s')) {
        this.cropArea.size = Math.max(100, this.originalCropArea.size + deltaY);
      }
      if (this.resizeHandle.includes('n')) {
        const newSize = Math.max(100, this.originalCropArea.size - deltaY);
        this.cropArea.y = this.originalCropArea.y + (this.originalCropArea.size - newSize);
        this.cropArea.size = newSize;
      }
    }
    
    // Keep crop area within canvas bounds
    this.cropArea.x = Math.max(0, Math.min(this.canvas.width - this.cropArea.size, this.cropArea.x));
    this.cropArea.y = Math.max(0, Math.min(this.canvas.height - this.cropArea.size, this.cropArea.y));
    
    // Ensure crop area doesn't go outside the canvas
    if (this.cropArea.x < 0) this.cropArea.x = 0;
    if (this.cropArea.y < 0) this.cropArea.y = 0;
    if (this.cropArea.x + this.cropArea.size > this.canvas.width) {
      this.cropArea.x = this.canvas.width - this.cropArea.size;
    }
    if (this.cropArea.y + this.cropArea.size > this.canvas.height) {
      this.cropArea.y = this.canvas.height - this.cropArea.size;
    }
  }

  private onTouchEnd(): void {
    this.isResizing = false;
    this.isDragging = false;
    this.cleanupEventListeners();
  }

  private cleanupEventListeners(): void {
    // Remove all event listeners to prevent memory leaks and restore normal page behavior
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
    document.removeEventListener('touchmove', this.boundTouchMove);
    document.removeEventListener('touchend', this.boundTouchEnd);
  }

  async applyCrop(event?: MouseEvent): Promise<void> {
    if (event) event.stopPropagation();
    if (!this.canvas || !this.originalImage) return;

    const cropSize = this.cropArea.size;
    const output = document.createElement('canvas');
    // Use a higher resolution for the output (e.g., 512px minimum)
    const outputSize = Math.max(512, cropSize * 2);
    output.width = outputSize;
    output.height = outputSize;
    const octx = output.getContext('2d');
    if (!octx) return;

    /* Fill first, so anything the logo does not cover carries its own
       background colour instead of a transparent hole. */
    if (this.padColor) {
      octx.fillStyle = this.padColor;
      octx.fillRect(0, 0, outputSize, outputSize);
    }

    // Image placement data on main canvas
    const zoomedWidth = this.originalImage.width * this.zoom;
    const zoomedHeight = this.originalImage.height * this.zoom;
    const offsetX = (this.canvas.width - zoomedWidth) / 2; // image top-left on canvas
    const offsetY = (this.canvas.height - zoomedHeight) / 2;

    // Crop rect in canvas space
    const cropX = this.cropArea.x;
    const cropY = this.cropArea.y;
    const cropEndX = cropX + cropSize;
    const cropEndY = cropY + cropSize;

    // Image rect in canvas space
    const imgX = offsetX;
    const imgY = offsetY;
    const imgEndX = imgX + zoomedWidth;
    const imgEndY = imgY + zoomedHeight;

    // Intersection of crop area and image area (canvas coords)
    const interX = Math.max(cropX, imgX);
    const interY = Math.max(cropY, imgY);
    const interEndX = Math.min(cropEndX, imgEndX);
    const interEndY = Math.min(cropEndY, imgEndY);
    const interW = interEndX - interX;
    const interH = interEndY - interY;

    if (interW > 0 && interH > 0) {
      // Map intersection back to original image coordinates
      const srcX = (interX - imgX) / this.zoom;
      const srcY = (interY - imgY) / this.zoom;
      const srcW = interW / this.zoom;
      const srcH = interH / this.zoom;

      // Scale the destination to match the higher resolution output
      const scale = outputSize / cropSize;
      const destX = (interX - cropX) * scale;
      const destY = (interY - cropY) * scale;
      const destW = interW * scale;
      const destH = interH * scale;

      octx.drawImage(
        this.originalImage,
        srcX, srcY, srcW, srcH,
        destX, destY, destW, destH
      );
    }

    /*
     * Awaited rather than left in a callback, so this method finishes when the
     * upload does. It used to resolve the moment the export was queued, which
     * made "has the logo saved yet" unanswerable from outside - including to a
     * test asserting on the file it produced.
     *
     * Any region the logo does not reach keeps whatever the fill left there:
     * its own background colour, or transparency when the source had it.
     */
    const blob = await new Promise<Blob | null>((resolve) =>
      output.toBlob(resolve, 'image/png')
    );
    if (!blob) {
      this.errorMessage = 'That image could not be exported. Please try again.';
      return;
    }

    const croppedFile = new File([blob], this.selectedFile?.name || 'logo.png', {
      type: 'image/png',
    });
    this.cleanupEventListeners(); // Clean up any active event listeners
    this.showCropper = false;
    this.isUploading = true;
    try {
      const downloadURL = await this.firebaseStorage.uploadFile(croppedFile, this.eventId);
      if (downloadURL) {
        this.logoUrl = downloadURL;
        this.logoUploaded.emit(downloadURL);
      } else {
        this.errorMessage = 'Failed to upload image. Please try again.';
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      this.errorMessage = 'Failed to upload image. Please try again.';
    } finally {
      this.isUploading = false;
    }
  }

  cancelCrop(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    console.log('Cancel crop clicked!');
    this.cleanupEventListeners(); // Clean up any active event listeners
    this.showCropper = false;
    this.selectedFile = null;
    this.originalImage = null;
    this.zoom = 1;
  }

  onImageError(event: Event): void {
    console.error('Error loading image:', event);
    this.logoUrl = null;
  }

  removeLogo(): void {
    this.logoUrl = null;
    this.logoRemoved.emit();
  }

  // Helpers for shaded regions
  getRightShadeWidth(): number {
    return Math.max(0, this.canvasWidth - (this.cropArea.x + this.cropArea.size));
  }
  getBottomShadeHeight(): number {
    return Math.max(0, this.canvasHeight - (this.cropArea.y + this.cropArea.size));
  }
}